/**
 * Local-only TanStack DB collections for local practice, plus the one-time
 * conversion of the legacy Dexie Y.Doc into rows.
 *
 * Local practice has no sync DO: its rows live in these collections
 * (seeded from the Dexie `localProjects` store, persisted back by the pool)
 * and are mutated by the shared mutator functions applied directly.
 *
 * Also exports `emptyCollections`: a frozen, never-written set used as the
 * fallback when a project has no session (e.g. sidebar rows for unopened
 * projects), so hooks can query unconditionally.
 */

import * as Y from 'yjs';
import { createCollection, localOnlyCollectionOptions, type Collection } from '@tanstack/db';
import {
  answerRowId,
  reconciliationRowId,
  syncSchema,
  type AnnotationRow,
  type AnswerRow,
  type AppraisalRow,
  type ChecklistRow,
  type OutcomeRow,
  type PdfRow,
  type ReconciliationRow,
  type StudyRow,
} from '@corates/shared/sync';

/** The uniform per-project collection set the read hooks consume. */
export interface ProjectCollections {
  studies: Collection<StudyRow>;
  checklists: Collection<ChecklistRow>;
  appraisals: Collection<AppraisalRow>;
  answers: Collection<AnswerRow>;
  annotations: Collection<AnnotationRow>;
  outcomes: Collection<OutcomeRow>;
  pdfs: Collection<PdfRow>;
  reconciliations: Collection<ReconciliationRow>;
}

function localSet(idPrefix: string): ProjectCollections {
  const make = <T extends { id: string }>(table: string) =>
    createCollection(
      localOnlyCollectionOptions({
        id: `${idPrefix}-${table}`,
        getKey: (row: T) => row.id,
      }),
    ) as unknown as Collection<T>;
  return {
    studies: make<StudyRow>('studies'),
    checklists: make<ChecklistRow>('checklists'),
    appraisals: make<AppraisalRow>('appraisals'),
    answers: make<AnswerRow>('answers'),
    annotations: make<AnnotationRow>('annotations'),
    outcomes: make<OutcomeRow>('outcomes'),
    pdfs: make<PdfRow>('pdfs'),
    reconciliations: make<ReconciliationRow>('reconciliations'),
  };
}

export function createLocalCollections(projectId: string): ProjectCollections {
  return localSet(`local-${projectId}-${Date.now()}`);
}

export const emptyCollections: ProjectCollections = localSet('empty-fallback');

function plainValue(value: unknown): unknown {
  if (value instanceof Y.Text) return value.toString();
  if (value instanceof Y.AbstractType) {
    return (value as unknown as { toJSON: () => unknown }).toJSON();
  }
  return value;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

/** The tables local practice persists; annotations and pdfs stay in memory. */
export const LOCAL_TABLES = [
  'studies',
  'checklists',
  'appraisals',
  'answers',
  'outcomes',
  'reconciliations',
] as const;
export type LocalTable = (typeof LOCAL_TABLES)[number];

/** Persisted rows as written by any past build: later tables may be absent. */
export interface LocalRows {
  studies: unknown[];
  checklists: unknown[];
  answers: unknown[];
  outcomes?: unknown[];
  reconciliations?: unknown[];
  appraisals?: unknown[];
}

/** Rows after `migrateLocalRows`: every table present, every row validated. */
export type MigratedLocalRows = Required<LocalRows>;

type StandardSchema = { '~standard': { validate: (v: unknown) => unknown } };

export function standardParse(schema: StandardSchema, value: unknown): unknown {
  const result = schema['~standard'].validate(value) as {
    value?: unknown;
    issues?: Array<{ message: string }>;
  };
  if (result.issues) {
    throw new Error(`local row validation failed: ${result.issues[0]?.message ?? 'invalid'}`);
  }
  return result.value;
}

/** Parses a row through its table schema, applying defaults, as the server does at flush. */
export function validateLocalRow(table: string, row: unknown): unknown {
  const tableSchema = (syncSchema.tables as Record<string, StandardSchema>)[table];
  return tableSchema ? standardParse(tableSchema, row) : row;
}

/** The transaction shape the shared mutators and migrations run against. */
export interface LocalTx {
  get(tbl: string, id: string): unknown;
  list(
    tbl: string,
    options?: { where?: Record<string, unknown> },
  ): Array<{ id: string; data: unknown }>;
  put(tbl: string, id: string, data: unknown): void;
  del(tbl: string, id: string): void;
}

/**
 * A mutator transaction over local-only collections. Reads see earlier
 * writes because these collections apply synchronously. An existing row is
 * updated in place rather than deleted and reinserted: the collection
 * confirms writes asynchronously, and two delete-plus-insert cycles on one
 * key in a single mutation settle as a net delete.
 */
export function localTx(
  collections: ProjectCollections,
  onWrite?: (tbl: string, id: string) => void,
): LocalTx {
  const cols = collections as unknown as Record<
    string,
    {
      get(id: string): unknown;
      has(id: string): boolean;
      insert(row: unknown): void;
      update(id: string, callback: (draft: Record<string, unknown>) => void): void;
      delete(id: string): void;
      toArray: Array<{ id: string }>;
    }
  >;
  return {
    get: (tbl, id) => cols[tbl]?.get(id) ?? null,
    list: (tbl, options) => {
      const where = Object.entries(options?.where ?? {}).filter(([, v]) => v !== undefined);
      return (cols[tbl]?.toArray ?? [])
        .filter(row => where.every(([field, v]) => (row as Record<string, unknown>)[field] === v))
        .map(row => ({ id: row.id, data: row }));
    },
    put: (tbl, id, data) => {
      const validated = validateLocalRow(tbl, data) as Record<string, unknown>;
      const col = cols[tbl];
      if (!col) return;
      onWrite?.(tbl, id);
      if (!col.has(id)) {
        col.insert(validated);
        return;
      }
      col.update(id, draft => {
        // Deleting a draft key is not tracked as a change; undefined is.
        for (const key of Object.keys(draft)) {
          if (!(key in validated) && !key.startsWith('$')) draft[key] = undefined;
        }
        Object.assign(draft, validated);
      });
    },
    del: (tbl, id) => {
      const col = cols[tbl];
      if (!col?.has(id)) return;
      col.delete(id);
      onWrite?.(tbl, id);
    },
  };
}

/** One-time conversion of a legacy local-practice Y.Doc into plain rows. */
export function rowsFromLocalDoc(ydoc: Y.Doc): LocalRows {
  const reviews = ydoc.getMap('reviews');
  {
    const studies = new Map<string, StudyRow>();
    // Version 1 shape: `kind` did not exist; migrateLocalRows derives it.
    const checklists = new Map<string, Omit<ChecklistRow, 'kind'>>();
    const answers = new Map<string, AnswerRow>();
    const outcomes: OutcomeRow[] = [];
    const reconciliations: ReconciliationRow[] = [];

    const outcomesMap = ydoc.getMap('meta').get('outcomes');
    if (outcomesMap instanceof Y.Map) {
      for (const [outcomeId, value] of outcomesMap.entries()) {
        const outcome = plainValue(value) as Record<string, unknown> | null;
        if (!outcome || typeof outcome !== 'object') continue;
        outcomes.push({
          id: outcomeId,
          name: asString(outcome.name),
          createdAt: asNumber(outcome.createdAt),
          createdBy: asString(outcome.createdBy, 'local'),
        });
      }
    }

    for (const [studyId, studyValue] of reviews.entries()) {
      const study = studyValue as Y.Map<unknown>;
      if (!(study instanceof Y.Map)) continue;
      studies.set(studyId, {
        id: studyId,
        name: asString(study.get('name')),
        description: asString(study.get('description')),
        createdAt: asNumber(study.get('createdAt')),
        updatedAt: asNumber(study.get('updatedAt')),
      } as StudyRow);

      const checklistsMap = study.get('checklists');
      if (!(checklistsMap instanceof Y.Map)) continue;
      for (const [checklistId, checklistValue] of checklistsMap.entries()) {
        const checklist = checklistValue as Y.Map<unknown>;
        if (!(checklist instanceof Y.Map)) continue;
        const type = asString(checklist.get('type'), 'AMSTAR2') as ChecklistRow['type'];
        checklists.set(checklistId, {
          id: checklistId,
          studyId,
          type,
          title: asString(checklist.get('title')),
          assignedTo: (checklist.get('assignedTo') as string | null) ?? null,
          status: asString(checklist.get('status'), 'pending') as ChecklistRow['status'],
          outcomeId: (checklist.get('outcomeId') as string | null) ?? null,
          createdAt: asNumber(checklist.get('createdAt')),
          updatedAt: asNumber(checklist.get('updatedAt')),
        });

        const answersMap = checklist.get('answers');
        if (!(answersMap instanceof Y.Map)) continue;
        for (const [flatKey, rawValue] of answersMap.entries()) {
          const id = answerRowId(checklistId, flatKey);
          answers.set(id, {
            id,
            studyId,
            checklistId,
            key: flatKey,
            value: plainValue(rawValue) as AnswerRow['value'],
          });
        }
      }

      const reconciliationsMap = study.get('reconciliations');
      if (reconciliationsMap instanceof Y.Map) {
        for (const [outcomeKey, value] of reconciliationsMap.entries()) {
          const progress = plainValue(value) as Record<string, unknown> | null;
          if (!progress || typeof progress !== 'object') continue;
          reconciliations.push({
            id: reconciliationRowId(studyId, outcomeKey),
            studyId,
            outcomeKey,
            outcomeId: (progress.outcomeId as string | null) ?? null,
            type: asString(progress.type, 'AMSTAR2') as ReconciliationRow['type'],
            checklist1Id: asString(progress.checklist1Id),
            checklist2Id: asString(progress.checklist2Id),
            ...(typeof progress.reconciledChecklistId === 'string' ?
              { reconciledChecklistId: progress.reconciledChecklistId }
            : {}),
            ...(typeof progress.currentPage === 'number' ?
              { currentPage: progress.currentPage }
            : {}),
            ...(typeof progress.viewMode === 'string' ? { viewMode: progress.viewMode } : {}),
            updatedAt: asNumber(progress.updatedAt),
          } as ReconciliationRow);
        }
      }
    }

    return {
      studies: [...studies.values()],
      checklists: [...checklists.values()],
      answers: [...answers.values()],
      outcomes,
      reconciliations,
    };
  }
}

/** Seed freshly created collections from migrated rows. */
export function seedLocalCollections(
  collections: ProjectCollections,
  rows: MigratedLocalRows,
): void {
  for (const row of rows.studies) collections.studies.insert(row as StudyRow);
  for (const row of rows.checklists) collections.checklists.insert(row as ChecklistRow);
  for (const row of rows.appraisals) collections.appraisals.insert(row as AppraisalRow);
  for (const row of rows.answers) collections.answers.insert(row as AnswerRow);
  for (const row of rows.outcomes) collections.outcomes.insert(row as OutcomeRow);
  for (const row of rows.reconciliations) {
    collections.reconciliations.insert(row as ReconciliationRow);
  }
}
